/**
 * "Recording ongoing monitoring" tour.
 *
 * Walks through the Monitoring section on a client detail page — what it's
 * for, the categories available, and how to add a note.
 *
 * Targets:
 *   tr.clickable                          — any client row
 *   data-tour="section-tab-monitoring"    — Monitoring section nav button
 *   data-tour="add-note-btn"              — "Add Note" button
 *   data-tour="save-note"                 — "Save Note" button in modal
 *
 * Categories (Observation / Review Note / Client Contact / Change in
 * Circumstances / Other) mirror ClientMonitoring.vue's category dropdown
 * (lines 33-37). Keep in sync.
 */

import { NEXT, BACK, START, SKIP, DONE, advanceWhenAppears } from './_helpers.js';

function waitForElement(selector, timeout = 10000) {
    return new Promise((resolve) => {
        if (document.querySelector(selector)) return resolve();
        const start = Date.now();
        const check = () => {
            if (document.querySelector(selector)) return resolve();
            if (Date.now() - start > timeout) return resolve();
            setTimeout(check, 50);
        };
        check();
    });
}

export const ongoingMonitoringTour = {
    id: 'ongoing-monitoring',
    title: 'Recording ongoing monitoring',
    description: 'How to log monitoring notes against a client — observations, review notes, client contact, and changes in circumstances.',
    autoLaunchOn: { page: 'clients' },

    steps: [
        {
            id: 'intro',
            title: 'Ongoing monitoring',
            text: `Ongoing monitoring is the AML obligation to keep tabs on each client
                between formal reviews — noting unusual transactions or behaviour, recording
                contact, capturing changes in their circumstances, and so on.
                <br><br>
                The Monitoring section on each client's detail page is where you log
                these observations. Anyone with access to the client can add notes.`,
            buttons: [START],
        },
        {
            id: 'pick-row',
            attachTo: { element: 'table.aml_table tbody tr.clickable', on: 'top' },
            beforeShowPromise: () => waitForElement('table.aml_table tbody tr.clickable'),
            title: 'Open a client',
            text: `Click any row to open that client's detail page.`,
            // See editClient.js for why we watch for the destination
            // marker instead of relying on advanceOn-on-row-click.
            when: advanceWhenAppears('[data-tour="section-tab-monitoring"]'),
        },
        {
            id: 'section-tab',
            attachTo: { element: '[data-tour="section-tab-monitoring"]', on: 'bottom' },
            beforeShowPromise: () => waitForElement('[data-tour="section-tab-monitoring"]'),
            title: 'Open the Monitoring section',
            text: `Click <strong>Monitoring</strong> in the section nav. You'll see all
                existing notes for this client, sorted newest first, with the author and
                date stamp on each.`,
            advanceOn: { selector: '[data-tour="section-tab-monitoring"]', event: 'click' },
        },
        {
            id: 'add-note',
            attachTo: { element: '[data-tour="add-note-btn"]', on: 'left' },
            beforeShowPromise: () => waitForElement('[data-tour="add-note-btn"]'),
            title: 'Add a note',
            text: `Click <strong>Add Note</strong> to log a new monitoring entry.`,
            advanceOn: { selector: '[data-tour="add-note-btn"]', event: 'click' },
        },
        {
            id: 'category',
            attachTo: { element: '[data-tour="note-category-field"]', on: 'right' },
            beforeShowPromise: () => waitForElement('[data-tour="save-note"]'),
            // Cancel if the modal closes (X or Cancel button) — save-note
            // lives inside the modal and goes away with it.
            cancelIfMissing: '[data-tour="save-note"]',
            title: 'Pick a category',
            text: `<strong>Category</strong> places the note into one of five buckets:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>Observation</strong> — default. General compliance
                        observation about the client.</li>
                    <li><strong>Review Note</strong> — captured during a periodic
                        review.</li>
                    <li><strong>Client Contact</strong> — record of a phone call,
                        email exchange, or meeting.</li>
                    <li><strong>Change in Circumstances</strong> — anything material
                        about the client that's changed (address, ownership, services).</li>
                    <li><strong>Other</strong> — for anything that doesn't fit above.</li>
                </ul>`,
            // No Previous button here — the prior tour step lives outside
            // the modal that the user has just opened. Going back to it
            // while the modal is still on screen reads as broken.
            buttons: [NEXT],
        },
        {
            id: 'note-content',
            attachTo: { element: '[data-tour="note-content-field"]', on: 'right' },
            cancelIfMissing: '[data-tour="save-note"]',
            title: 'Write the note',
            text: `<strong>Note</strong> (required) is free-text. Be specific and
                factual — the note becomes part of the audit trail. If something you
                observe rises to the level of a compliance concern, raise an
                <strong>escalation</strong> separately (top of the client detail page,
                Raise Escalation button) — monitoring notes themselves don't trigger
                escalation review automatically.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'finish',
            attachTo: { element: '[data-tour="save-note"]', on: 'top' },
            cancelIfMissing: '[data-tour="save-note"]',
            title: 'Save when ready',
            text: `Click <strong>Save Note</strong> to record. The note appears at the
                top of the list immediately, with your name and the timestamp. Notes
                can't be edited or deleted from this view — they're an immutable
                audit record.`,
            buttons: [BACK, DONE],
        },
    ],
};
